import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { ArrowBack, EditOutlined, VisibilityOutlined } from '@mui/icons-material';
import QRCode from 'qrcode';
import api from '../services/api';
import { toDisplayCard } from '../utils/cardDataStructure';
import NfcPrintThemeCanvas from '../components/nfc-print/NfcPrintThemeCanvas';
import { fmtDate } from '../utils/dateFormat';
import { galleryFitMaxWidthForTheme } from '../utils/nfcPrintGalleryFit';

const APP_ORIGIN = 'https://app.webcard.digital';

const GALLERY_PREVIEW_H = 226;

function mergePreview(cardData, overrides) {
  const out = { ...cardData };
  if (!overrides || typeof overrides !== 'object') return out;
  for (const k of ['name', 'title', 'company', 'bannerImageUrl']) {
    if (!Object.prototype.hasOwnProperty.call(overrides, k)) continue;
    const raw = overrides[k];
    if (raw == null || String(raw).trim() === '') {
      delete out[k];
    } else {
      out[k] = String(raw).trim();
    }
  }
  return out;
}

const MyNfcPrintDesigns = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [designs, setDesigns] = useState([]);
  const [cardDataById, setCardDataById] = useState({});
  const [qrByCardId, setQrByCardId] = useState({});
  const [previewItem, setPreviewItem] = useState(null);
  const [subData, setSubData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError(null);
        const [{ data: mine }, { data: cardsData }, { data: sub }] = await Promise.all([
          api.get('/nfc/print-designs/mine'),
          api.get('/cards/my-cards', { params: { limit: 100 } }),
          api.get('/payments/subscription'),
        ]);
        if (cancelled) return;
        setSubData(sub || null);
        const list = mine?.success && Array.isArray(mine.designs) ? mine.designs : [];
        setDesigns(list);

        const map = {};
        for (const row of cardsData?.cards || []) {
          let raw = row.cardData ?? row.card_data;
          if (typeof raw === 'string') {
            try {
              raw = JSON.parse(raw);
            } catch {
              raw = {};
            }
          }
          map[row.id] = toDisplayCard(raw || {});
        }
        setCardDataById(map);
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.message || e.message || 'Failed to load designs');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const cardIdsForQr = useMemo(
    () => [...new Set(designs.map((d) => d.userCardId).filter(Boolean))],
    [designs]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = {};
      for (const cardId of cardIdsForQr) {
        const design = designs.find((d) => d.userCardId === cardId);
        const slug = design?.cardUrl;
        if (!slug) continue;
        try {
          const url = await QRCode.toDataURL(`${APP_ORIGIN}/${slug}`, {
            width: 280,
            margin: 1,
            color: { dark: '#000000', light: '#ffffff' },
          });
          next[cardId] = url;
        } catch {
          next[cardId] = '';
        }
      }
      if (!cancelled) setQrByCardId((prev) => ({ ...prev, ...next }));
    })();
    return () => {
      cancelled = true;
    };
  }, [cardIdsForQr, designs]);

  const previewCardData = (item) => {
    const base = cardDataById[item.userCardId] || {};
    return mergePreview(base, item.layoutOverrides);
  };

  const openEditor = (item) => {
    const themeId = item.theme?.id || item.printThemeId;
    navigate(`/nfc/print-designs?cardId=${item.userCardId}&themeId=${themeId}`);
  };

  const nfcLimit = subData?.limits?.nfc ?? 0;
  const nfcUsed = subData?.usage?.nfcDesigns ?? designs.length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: '100%', bgcolor: 'background.default', py: 4, px: { xs: 2, md: 3 } }}>
      <Box sx={{ maxWidth: { xs: '100%', lg: 1320 }, mx: 'auto' }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/nfc/print-designs')}
          sx={{ mb: 2, textTransform: 'none' }}
        >
          Back to print designs
        </Button>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            My NFC print designs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Saved physical card layouts linked to your digital cards.
            {nfcLimit > 0 ? ` ${nfcUsed} of ${nfcLimit} design${nfcLimit > 1 ? 's' : ''} used on your plan.` : ''}
          </Typography>
        </Box>

        {nfcLimit === 0 && (
          <Alert
            severity="warning"
            sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={() => navigate('/subscription')} sx={{ textTransform: 'none', fontWeight: 600 }}>
                View plans
              </Button>
            }
          >
            Upgrade to Pro or Business to create NFC print designs on your account.
          </Alert>
        )}

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {!error && designs.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No saved designs yet
            </Typography>
            <Typography variant="body2" color="text.disabled" sx={{ mb: 3, maxWidth: 420, mx: 'auto' }}>
              Pick a print theme, customize it for a digital card, and save — your designs will appear here.
            </Typography>
            <Button variant="outlined" onClick={() => navigate('/nfc/print-designs')} sx={{ textTransform: 'none' }}>
              Back to print designs
            </Button>
          </Box>
        )}

        <Grid container spacing={2.5} alignItems="stretch">
          {designs.map((item) => {
            const themeJson = item.theme?.themeJson;
            if (!themeJson || typeof themeJson !== 'object') return null;
            const pw = Number(themeJson.canvas?.width) || 2102;
            const ph = Number(themeJson.canvas?.height) || 1346;
            const galleryFitW = galleryFitMaxWidthForTheme(themeJson, GALLERY_PREVIEW_H);
            const title = item.theme?.name || 'Print design';
            const cardLabel = item.cardUrl || `Card #${item.userCardId}`;
            const prev = previewCardData(item);
            const qr = qrByCardId[item.userCardId] || '';

            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.id} sx={{ display: 'flex', minWidth: 0, maxWidth: '100%' }}>
                <Card
                  variant="outlined"
                  sx={{
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    borderColor: 'divider',
                    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                    '&:hover': { borderColor: 'primary.main', boxShadow: '0 6px 24px rgba(0,0,0,0.08)' },
                  }}
                >
                  <Box
                    sx={{
                      height: GALLERY_PREVIEW_H,
                      flexShrink: 0,
                      bgcolor: 'grey.100',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      px: 1,
                      width: '100%',
                      maxWidth: '100%',
                      minWidth: 0,
                      boxSizing: 'border-box',
                    }}
                  >
                    <NfcPrintThemeCanvas
                      theme={themeJson}
                      cardData={prev}
                      qrDataUrl={qr}
                      faceIds={['front']}
                      showFaceLabels={false}
                      maxWidth="none"
                      cardPixelSize={{ width: pw, height: ph }}
                      previewStackVertical
                      fitMaxWidth={galleryFitW}
                    />
                  </Box>
                  <Box sx={{ px: 2, py: 1.5, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5, minWidth: 0 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, minWidth: 0 }} noWrap>
                        {title}
                      </Typography>
                      {item.theme?.category ? (
                        <Chip label={item.theme.category} size="small" variant="outlined" sx={{ height: 20, flexShrink: 0 }} />
                      ) : null}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                      Digital card: {cardLabel}
                    </Typography>
                    {item.updatedAt ? (
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
                        Updated {fmtDate(item.updatedAt)}
                      </Typography>
                    ) : null}
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityOutlined fontSize="small" />}
                        onClick={() => setPreviewItem(item)}
                        sx={{ textTransform: 'none', flex: 1 }}
                      >
                        Preview
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<EditOutlined fontSize="small" />}
                        onClick={() => openEditor(item)}
                        sx={{ textTransform: 'none', flex: 1 }}
                      >
                        Edit
                      </Button>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Dialog
          open={!!previewItem}
          onClose={() => setPreviewItem(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {previewItem?.theme?.name || 'Print preview'} — {previewItem?.cardUrl}
          </DialogTitle>
          <DialogContent dividers sx={{ bgcolor: 'grey.100' }}>
            {previewItem?.theme?.themeJson ? (
              <NfcPrintThemeCanvas
                theme={previewItem.theme.themeJson}
                cardData={previewCardData(previewItem)}
                qrDataUrl={qrByCardId[previewItem.userCardId] || ''}
                showFaceLabels
                cardPixelSize={{
                  width: Number(previewItem.theme.themeJson.canvas?.width) || 2102,
                  height: Number(previewItem.theme.themeJson.canvas?.height) || 1346,
                }}
                previewStackVertical
                fitMaxWidth={720}
              />
            ) : null}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setPreviewItem(null)} sx={{ textTransform: 'none' }}>
              Close
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                if (previewItem) openEditor(previewItem);
                setPreviewItem(null);
              }}
              sx={{ textTransform: 'none' }}
            >
              Edit design
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default MyNfcPrintDesigns;
